import {
  Accordion,
  Button,
  Group,
  JsonInput,
  Radio,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeAllModals } from '@mantine/modals';
import { IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkIsUrl, APIPropertyFunction, APIPropertyItemFunction, RunApiResponseFunction } from '@mygpt/app';
import { checkIsPath } from '@/utils/helper';
import classes from './edit.module.css';

export interface ApiItemEditProps {
  api?: APIPropertyItemFunction;

  onSubmit?: (api: APIPropertyFunction) => Promise<void>;
  onRun?: (question: string, api: APIPropertyFunction) => Promise<RunApiResponseFunction>;
}

interface RunLog {
  success: boolean;
  question: string;
  keywords: string;
  sendInfo: string;
  answer: string;
  message: string;
  createdAt: string;
}

export const ApiItemEdit: FC<ApiItemEditProps> = ({ api, onRun, onSubmit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [paramsType, setParamsType] = useState('');

  const form = useForm({
    initialValues: {
      function_call_name: api?.function_call_name ?? '',
      function_call_description: api?.function_call_description ?? '',
      openapi_url: api?.openapi_url ?? '',
      body: api?.body ? JSON.stringify(api.body) : '',
      body_required: api?.body_required ? JSON.stringify(api.body_required) : '',
      request_explanation: api?.request_explanation ?? '',
      header_authorization: api?.header_authorization ? JSON.stringify(api.header_authorization) : '',
      method: api?.method ?? 'get',
      server_url: api?.server_url ?? '',
      path: api?.path ?? '',
    },
    validate: {
      openapi_url: (value) => {
        if (paramsType === 'url') {
          return value ? (checkIsUrl(value.trim()) ? null : 'Invalid URL') : 'Invalid URL';
        }
        return null;
      },
      body: (value) => {
        if (paramsType === 'description') {
          if (!value) return 'Required';
          try {
            JSON.parse(value);
            return null;
          } catch {
            return t('fields.json_invalid');
          }
        }
        return null;
      },
      body_required: (value) => {
        if (paramsType === 'description') {
          if (!value) return 'Required';
          try {
            JSON.parse(value);
            return null;
          } catch {
            return t('fields.json_invalid');
          }
        }
        return null;
      },
      header_authorization: (value) => {
        // 非必填，但填写时必须是合法 JSON，避免非法值提交致后端 500
        if (!value) return null;
        try {
          JSON.parse(value);
          return null;
        } catch {
          return t('fields.json_invalid');
        }
      },
      function_call_name: (value) => {
        return value ? null : 'Invalid Name';
      },
      method: (value) => {
        return value ? null : 'Invalid Method';
      },
      server_url: (value) => {
        return checkIsUrl(value) ? null : 'Invalid URL';
      },
      path: (value) => {
        return checkIsPath(value) ? null : 'Invalid Path';
      },
    },
  });

  const runForm = useForm({
    initialValues: {
      question: '',
    },
    validate: {
      question: (value) => {
        return value ? null : t('fields.question_invalid');
      },
    },
  });

  const runFormRef = useRef<HTMLFormElement | null>(null);

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    if (paramsType === 'url') {
      values.body = '{}';
      values.body_required = '[]';
    } else {
      values.openapi_url = '';
    }

    onSubmit?.(values).finally(() => {
      setLoading(false);
    });
  };

  const [logs, setLogs] = useState<RunLog[]>([]);

  const handleRun = async (values: typeof runForm.values) => {
    if (form.validate().hasErrors) return;
    setRunLoading(true);

    onRun?.(values.question, {
      ...form.values,
      header_authorization: form.values.header_authorization ? JSON.parse(form.values.header_authorization) : {},
    })
      .then((res) => {
        const log = {
          success: res.function_call_sta,
          question: values.question,
          keywords: res.keywords,
          sendInfo: JSON.stringify(res.send_info),
          answer: res.ask_content.answer,
          message: (res.ask_content.messages ?? []).join('\n'),
          createdAt: new Date().toISOString(),
        };
        setLogs((logs) => [...logs, log]);
      })
      .finally(() => {
        setRunLoading(false);
      });
  };

  const handleRunSubmit = async () => {
    const submit = runForm.onSubmit(handleRun);
    submit();
  };

  const leftRef = useRef<HTMLDivElement | null>(null);
  const [leftHeight, setLeftHeight] = useState(0);

  useEffect(() => {
    setParamsType('description');
    api?.openapi_url && setParamsType('url');
    api?.body && setParamsType('description');

    const el = leftRef.current;
    const resizeObserver = new ResizeObserver(() => {
      if (el) {
        setLeftHeight(el.clientHeight);
      }
    });
    if (el) resizeObserver.observe(el);
    return () => {
      if (el) resizeObserver.unobserve(el);
    };
  }, []);

  const scrollAreaHeight = useMemo(() => {
    return leftHeight - 16 - 16 - 10 - 36;
  }, [leftHeight]);

  const ParamTootipLabel = `{
    "code": {
        "type": "string",
        "enum": ["100", "200"],
        "description": "code description"
    },
    "startTime": {
        "type": "string",
        "description": "start time description"
    },
    "type": {
        "type": "integer",
        "description": "type description"
    }
}`;

  const renderLog = (log: RunLog) => {
    return (
      <div className="flex" style={{ color: log.success ? '' : 'red' }}>
        <div className="mr-3 flex-none">{`${dayjs(log.createdAt).format('HH:mm:ss')}:`}</div>
        <div className="flex-1 whitespace-pre-line">
          <p>{log.question}</p>

          {log.success ? (
            <>
              <p>{typeof log.answer === 'object' ? JSON.stringify(log.answer) : log.answer || 'None'} </p>
              <p>{log.keywords}</p>
              <p>{log.sendInfo}</p>
            </>
          ) : (
            log.message
          )}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xs">
        <Stack gap="xs" className="mb-1">
          <TextInput
            label={t('apis.form.field_name')}
            placeholder={t('apis.form.field_name_placeholder')}
            {...form.getInputProps('function_call_name')}
          />
          <Textarea
            label={t('apis.form.field_description')}
            placeholder={t('apis.form.field_description_placeholder')}
            {...form.getInputProps('function_call_description')}
          />
        </Stack>
        <Group className="justify-between" wrap="nowrap">
          <Stack gap="xs" className="flex-1" ref={leftRef}>
            <Stack>
              <Accordion
                variant="separated"
                value={paramsType}
                chevron={null}
                onChange={(value) => {
                  if (!value) return;
                  setParamsType(value);
                }}
              >
                <Accordion.Item value="url" className={classes.accordionItem}>
                  <Accordion.Control>
                    <Radio checked={paramsType === 'url'} label={t('apis.form.field_open_url')} size="xs" readOnly />
                  </Accordion.Control>
                  <Accordion.Panel>
                    <TextInput
                      size="xs"
                      placeholder="https://www.youropenapisdocument.com"
                      {...form.getInputProps('openapi_url')}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
                <Accordion.Item value="description" className={classes.accordionItem}>
                  <Accordion.Control>
                    <Radio
                      checked={paramsType === 'description'}
                      label={
                        <Group gap={2}>
                          <Text size="xs" fw={500}>
                            {t('apis.form.params_description')}
                          </Text>
                          <Tooltip multiline withArrow label={<pre className="text-xs">{ParamTootipLabel}</pre>}>
                            <span>
                              <IconInfoCircle size={16} color="gray" />
                            </span>
                          </Tooltip>
                        </Group>
                      }
                      size="xs"
                      readOnly
                    />
                  </Accordion.Control>
                  <Accordion.Panel>
                    <JsonInput
                      validationError="Invalid json"
                      placeholder={t('apis.form.params_description_placeholder')}
                      formatOnBlur
                      autosize
                      minRows={4}
                      maxRows={8}
                      {...form.getInputProps('body')}
                    />
                    <JsonInput
                      className="mt-2"
                      label={
                        <Text size="xs" fw={500}>
                          {t('apis.form.required_params')}
                        </Text>
                      }
                      validationError="Invalid array"
                      placeholder='["type", "endTime", "startTime"]'
                      {...form.getInputProps('body_required')}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>

            <Textarea
              size="xs"
              label={t('apis.form.response_description')}
              placeholder={t('apis.form.response_description_placeholder')}
              {...form.getInputProps('request_explanation')}
            />
            <JsonInput
              label={
                <Text size="xs" fw={500}>
                  {t('apis.form.field_headers')}
                </Text>
              }
              placeholder='{"Token": "xxx", "Content-Type": "application/json"}'
              validationError="Invalid json"
              formatOnBlur
              autosize
              minRows={4}
              maxRows={8}
              {...form.getInputProps('header_authorization')}
            />
            <Select
              size="xs"
              label={t('apis.form.field_method')}
              data={[
                { value: 'get', label: 'GET' },
                { value: 'post', label: 'POST' },
                { value: 'put', label: 'PUT' },
                { value: 'delete', label: 'DELETE' },
              ]}
              {...form.getInputProps('method')}
            />
            <TextInput
              size="xs"
              label={t('apis.form.field_server_url')}
              placeholder="https://www.youropenapis.com"
              {...form.getInputProps('server_url')}
            />

            <TextInput
              size="xs"
              label={t('apis.form.field_path')}
              placeholder="/my-api"
              {...form.getInputProps('path')}
            />
          </Stack>
          <form ref={runFormRef} className="flex-1 self-stretch p-4 rounded-lg bg-violet-50 border border-violet-200">
            <Stack gap="xs">
              {/* <Textarea autosize minRows={4} /> */}
              <ScrollArea.Autosize
                mah={scrollAreaHeight}
                mih={scrollAreaHeight}
                mx="auto"
                className="text-left text-xs w-full"
              >
                {logs.map(renderLog)}
              </ScrollArea.Autosize>
              <Group className="items-start justify-end">
                <TextInput
                  className="flex-1"
                  placeholder={t('apis.form.field_test_content')}
                  {...runForm.getInputProps('question')}
                />
                <Button onClick={handleRunSubmit} loading={runLoading} variant="outline">
                  {t('apis.form.action_run')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Group>
        <div className="flex justify-end">
          <Group>
            <Button variant="light" onClick={() => closeAllModals()}>
              {t('alert_cancel')}
            </Button>
            <Button type="submit" loading={loading}>
              {t('save')}
            </Button>
          </Group>
        </div>
      </Stack>
    </form>
  );
};
